import { GetLocationsPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/emsp-bo/models/inputs/locations/get-locations-paginated.model';
import { useUrlSyncedFilters } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/hooks';
import { PullLocationsModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/back-office-gateway-emsp-lib/src/inputs/locations/pull-locations.model';
import { LocationModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/ocpi/src/ocpi-cpo-api/models/locations/location.model';
import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
} from 'react';
import { DeepPartialSkipArrayKey } from 'react-hook-form';

import {
  TABLE_LIMITS_OPTIONS,
  TABLE_LIMIT_OPTION_MIN,
} from '@app/config/app.config';
import { EMSP_BO_LOCATION_FILTERS_LSK } from '@app/config/localstorage-keys.const';

import {
  EMSP_BO_EMSP_LOCATION_LIST_SORT_FIELD,
  EMSP_BO_EMSP_LOCATION_LIST_SORT_ORDER,
  EMSP_BO_LOCATION_LIST_FILTER_NAME_LIST,
} from './emsp-bo-location-list/emsp-bo-location-list.config';

// /////////////////////////////////////////////////////////////////////////////

export const EmspBoLocationContext = createContext<LocationModel | null>(null);

export const useEmspBoLocationContext = (): LocationModel => {
  const context = useContext(EmspBoLocationContext);

  if (!context) {
    throw new Error(
      'useEmspBoLocationContext must be used within EmspBoLocationContext.Provider',
    );
  }

  return context;
};

interface EmspBoLocationContextProviderProps {
  data: LocationModel;
  children: React.ReactNode;
}

export const EmspBoLocationContextProvider = ({
  data,
  children,
}: EmspBoLocationContextProviderProps): React.JSX.Element => (
  <EmspBoLocationContext.Provider value={data}>
    {children}
  </EmspBoLocationContext.Provider>
);

// /////////////////////////////////////////////////////////////////////////////

export const PullEmspBoLocationContext =
  createContext<DeepPartialSkipArrayKey<PullLocationsModel> | null>(null);

export const usePullEmspBoLocationContext =
  (): DeepPartialSkipArrayKey<PullLocationsModel> => {
    const context = useContext(PullEmspBoLocationContext);

    if (!context) {
      throw new Error(
        'usePullEmspBoLocationContext must be used within PullEmspBoLocationContext.Provider',
      );
    }

    return context;
  };

interface PullEmspBoLocationContextProviderProps {
  data: DeepPartialSkipArrayKey<PullLocationsModel>;
  children: React.ReactNode;
}

export const PullEmspBoLocationContextProvider = ({
  data,
  children,
}: PullEmspBoLocationContextProviderProps): React.JSX.Element => (
  <PullEmspBoLocationContext.Provider value={data}>
    {children}
  </PullEmspBoLocationContext.Provider>
);

// /////////////////////////////////////////////////////////////////////////////

export interface EmspBoEmspLocationListContextProps {
  getPaginatedQueryParameters: GetLocationsPaginatedModel;
  setGetPaginatedQueryParameters: Dispatch<
    SetStateAction<GetLocationsPaginatedModel>
  >;
  getPaginatedLimitList: number[];
}

export const EmspBoEmspLocationListContext =
  createContext<EmspBoEmspLocationListContextProps | null>(null);

export const useEmspBoEmspLocationListContext =
  (): EmspBoEmspLocationListContextProps => {
    const context = useContext(EmspBoEmspLocationListContext);

    if (!context) {
      throw new Error(
        'useEmspBoEmspLocationListContext must be used within EmspBoEmspLocationListContext.Provider',
      );
    }

    return context;
  };

interface EmspBoEmspLocationListContextProviderProps {
  children: React.ReactNode;
}

export const EmspBoEmspLocationListContextProvider = ({
  children,
}: EmspBoEmspLocationListContextProviderProps): React.JSX.Element => {
  const [getPaginatedQueryParameters, setGetPaginatedQueryParameters] =
    useUrlSyncedFilters<GetLocationsPaginatedModel>(
      {
        limit: TABLE_LIMITS_OPTIONS.at(-1) ?? TABLE_LIMIT_OPTION_MIN,
        offset: 0,
        sortField: EMSP_BO_EMSP_LOCATION_LIST_SORT_FIELD,
        sortOrder: EMSP_BO_EMSP_LOCATION_LIST_SORT_ORDER,
        // Filterable fields
        cpoId: '',
        textToSearch: undefined,
        country: undefined,
        city: undefined,
        name: undefined,
      },
      [
        'limit',
        'offset',
        'sortField',
        'sortOrder',
        ...EMSP_BO_LOCATION_LIST_FILTER_NAME_LIST,
        'textToSearch',
      ],
      EMSP_BO_LOCATION_FILTERS_LSK,
    );

  const value = useMemo(
    () => ({
      getPaginatedQueryParameters,
      setGetPaginatedQueryParameters,
      getPaginatedLimitList: TABLE_LIMITS_OPTIONS,
    }),
    [getPaginatedQueryParameters, setGetPaginatedQueryParameters],
  );

  return (
    <EmspBoEmspLocationListContext.Provider value={value}>
      {children}
    </EmspBoEmspLocationListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const FilterEmspBoLocationListContext =
  createContext<DeepPartialSkipArrayKey<GetLocationsPaginatedModel> | null>(
    null,
  );

export const useFilterEmspBoLocationListContext =
  (): DeepPartialSkipArrayKey<GetLocationsPaginatedModel> => {
    const context = useContext(FilterEmspBoLocationListContext);

    if (!context) {
      throw new Error(
        'useFilterEmspBoLocationListContext must be used within FilterEmspBoLocationListContext.Provider',
      );
    }

    return context;
  };

interface FilterEmspBoLocationListContextProviderProps {
  data: DeepPartialSkipArrayKey<GetLocationsPaginatedModel>;
  children: React.ReactNode;
}

export const FilterEmspBoLocationListContextProvider = ({
  data,
  children,
}: FilterEmspBoLocationListContextProviderProps): React.JSX.Element => (
  <FilterEmspBoLocationListContext.Provider value={data}>
    {children}
  </FilterEmspBoLocationListContext.Provider>
);

// /////////////////////////////////////////////////////////////////////////////

export const FilterSearchEmspBoLocationListContext =
  createContext<DeepPartialSkipArrayKey<GetLocationsPaginatedModel> | null>(
    null,
  );

export const useFilterSearchEmspBoLocationListContext =
  (): DeepPartialSkipArrayKey<GetLocationsPaginatedModel> => {
    const context = useContext(FilterSearchEmspBoLocationListContext);

    if (!context) {
      throw new Error(
        'useFilterSearchEmspBoLocationListContext must be used within FilterSearchEmspBoLocationListContextProvider',
      );
    }

    return context;
  };

interface FilterSearchEmspBoLocationListContextProviderProps {
  data: DeepPartialSkipArrayKey<GetLocationsPaginatedModel>;
  children: React.ReactNode;
}

export const FilterSearchEmspBoLocationListContextProvider = ({
  data,
  children,
}: FilterSearchEmspBoLocationListContextProviderProps): React.JSX.Element => (
  <FilterSearchEmspBoLocationListContext.Provider value={data}>
    {children}
  </FilterSearchEmspBoLocationListContext.Provider>
);
